import React from 'react';
import { motion } from 'framer-motion';
import { cvData } from '../data/cvData';
import { uiTranslations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';

const HeroStat = ({ value, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3 + delay }}
    className="flex flex-col gap-1"
  >
    <span className="text-2xl md:text-3xl font-black text-accent tracking-tight leading-none">
      {value}
    </span>
    <span className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
      {label}
    </span>
  </motion.div>
);

export default function Hero() {
  const { language } = useLanguage();
  const data = cvData[language].personal;
  const t = uiTranslations[language].hero;

  return (
    <section 
      id="hero" 
      className="relative min-h-[90vh] flex flex-col justify-center px-6 md:px-12 max-w-7xl mx-auto overflow-hidden pt-32 lg:pt-0"
    >
      <div className="flex flex-col-reverse md:flex-row items-center gap-12 md:gap-24 relative z-10">
        <div className="w-full md:w-3/5 text-left">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-bold tracking-widest uppercase bg-slate-100 text-slate-500 rounded">
              {t.tag}
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 leading-[1.1] mb-8">
              {data.name}.<br />
              <span className="text-accent">{data.role}</span>
            </h1>
            <p className="text-xl text-slate-500 leading-relaxed mb-8 max-w-2xl font-light">
              {data.intro}
            </p>

            {/* Metrics Bar */}
            <div className="grid grid-cols-2 lg:flex lg:items-center gap-8 md:gap-12 mb-12 py-8 border-y border-slate-100">
              {t.stats.map((stat, index) => (
                <HeroStat 
                  key={index} 
                  value={stat.value} 
                  label={stat.label} 
                  delay={index * 0.1}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap gap-5"
          >
            <a
              href="#contact"
              className="px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:bg-accent transition-all duration-300 shadow-lg shadow-primary/10"
            >
              {t.ctaContact}
            </a>
            <a
              href="#documents"
              className="px-8 py-4 bg-white text-slate-900 border border-slate-200 font-semibold rounded-lg hover:border-accent hover:text-accent transition-all duration-300 flex items-center gap-3"
            >
              <SafeIcon name="FileText" />
              {t.ctaDocs}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="w-full md:w-2/5 flex justify-center"
        >
          <div className="relative group">
            <div className="absolute -inset-4 bg-accent/10 rounded-2xl rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
            <div className="relative w-64 h-80 md:w-80 md:h-[450px] overflow-hidden rounded-2xl shadow-2xl transition-all duration-700">
              <img
                src={data.photo}
                alt={data.name}
                className="w-full h-full object-cover transform scale-105 group-hover:scale-100 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent opacity-60"></div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}